import type { StorageUsage } from '~/gql/storage/storage'
import { useQuery } from '@apollo/client/react'
import { useTranslation } from 'react-i18next'
import { Badge } from '~/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '~/components/ui/table'
import { GQL_STORAGE_USAGE_BY_ORGANIZATION } from '~/gql/storage/storage'
import { client } from '~/helpers/apollo'
import { formatBytes } from '~/utils/bytes'

/**
 * Ce que la plateforme porte en stockage, organisation par organisation (#180).
 *
 * C'est la moitié « mesure » de l'issue, celle qui vaut pour tout le monde : le
 * plafond ne s'applique qu'au gratuit, mais le compteur tourne partout — sans
 * quoi on ne saurait pas ce que le stockage coûte, ni sur quels chiffres poser
 * un plafond ailleurs le jour venu. Le total est la ligne à surveiller avant la
 * bascule S3 (#47) : c'est le volume à déplacer.
 */
function OrgStorageTable() {
  const { t, i18n } = useTranslation()

  const { data, loading } = useQuery(GQL_STORAGE_USAGE_BY_ORGANIZATION, {
    client,
    fetchPolicy: 'network-only',
  })

  const rows = (data?.storageUsageByOrganization ?? []) as unknown as StorageUsage[]
  const totalBytes = rows.reduce((sum, row) => sum + row.usedBytes, 0)
  const totalModules = rows.reduce((sum, row) => sum + row.moduleCount, 0)

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">{t('storage.platform.title')}</h2>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t('storage.platform.help')}</p>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.organizations')}</TableHead>
              <TableHead className="text-right">{t('storage.used')}</TableHead>
              <TableHead className="text-right">{t('storage.quota')}</TableHead>
              <TableHead className="text-right">{t('common.routing.modules')}</TableHead>
              <TableHead>{t('billing.plan')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.orgId}>
                <TableCell className="font-medium">
                  {row.orgName}
                  <span className="ml-2 text-muted-foreground">{row.orgSlug}</span>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatBytes(row.usedBytes, i18n.language)}
                  {row.overQuota && (
                    <Badge variant="destructive" className="ml-2">{t('storage.overQuota.badge')}</Badge>
                  )}
                </TableCell>
                {/* Un tiret, et non « 0 » ou « illimité » : l'absence de plafond
                    est un fait sur la formule, pas une valeur de stockage. */}
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {row.quotaBytes == null ? '—' : formatBytes(row.quotaBytes, i18n.language)}
                </TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">
                  {row.moduleCount}
                </TableCell>
                <TableCell>{t(`billing.plan.${row.plan}`)}</TableCell>
              </TableRow>
            ))}
            {rows.length > 0 && (
              <TableRow className="font-medium">
                <TableCell>{t('billing.total')}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatBytes(totalBytes, i18n.language)}
                </TableCell>
                <TableCell />
                <TableCell className="text-right tabular-nums">{totalModules}</TableCell>
                <TableCell />
              </TableRow>
            )}
            {rows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  {t('billing.empty')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export default OrgStorageTable
